/* ============================================================
   EXON cards island — quick-add delegation for product cards.
   One document-level listener handles every [data-quick-add] on
   the page (home rails, catalog grid, vendor hubs). Uses the
   runtime contract: EXON.cart.add({ id, quantity }). Idempotent —
   guards against double-init when loaded by more than one section.
   ============================================================ */
(function () {
  'use strict';
  if (window.__exonCardsInit) return;
  window.__exonCardsInit = true;

  /* cashback chip coordinator (CB-S1b, EXO-5356/5769; CONTRACT.md §4 and
     ADJUDICATION.md §A at ~/.claude/peers/threads/drop/drop-cb-s1b/). One
     rule for every chip on the page: a value is shown only when the server
     instant and the device instant (EXON.cbClock, assets/cashback-rate.js)
     read the same text; otherwise it is hidden until they agree.
       - [data-cb-chip] chips (placements.js cards, product.js pack rows,
         doc-mode product-card.liquid) are repainted from their attributes
         with EXON.cbChipText; "" = hidden. No reader = hidden, empty.
       - legacy SSR chips (a product card's .cb-mini without data-cb-chip,
         printed before the cutover) are shown only while BOTH legs are
         before a valid cutover (EXON.cbLegacyBoth, §A.1). Once that fails
         they are marked data-cb-off and stay hidden until reload: no
         document rate is drawn into them.
       - a legacy chip with a store switch, [data-cbc-card][data-cbc-ends]:
         both legs past the end → the flip to data-cbc-base, marked
         data-cbc-done (never deregistered, §A.2); both before → shown
         unchanged, or hidden once flipped; the legs disagree → hidden.
     One timer at the first instant any chip can change on either leg (the
     cutover is among them, via cbChipNextDelayMs); re-checked whenever the
     page is seen again (a hidden tab's timers are throttled and sleep can
     pause or delay them). Producers that insert chips call
     EXON.cbChipsRefresh() afterwards. */
  (function () {
    var MAX_WAIT = 2147483647;
    /* a product card's chip that is not a [data-cb-chip], and any chip that
       carries the legacy store-switch marker wherever it sits */
    var LEGACY_SEL = '[data-product-card] .cb-mini:not([data-cb-chip]), .cb-mini[data-cbc-card]';
    var timer = null;
    function clock() {
      var E = window.EXON;
      if (E && typeof E.cbClock === 'function') return E.cbClock();
      var now = Date.now();
      return { server: now, device: now };
    }
    function paint() {
      var E = window.EXON;
      var read = E && typeof E.cbChipText === 'function' ? E.cbChipText : null;
      Array.prototype.forEach.call(document.querySelectorAll('[data-cb-chip]'), function (el) {
        var text = read
          ? read(el.getAttribute('data-cb-handle') || '', el.getAttribute('data-cb-variant') || '',
              el.getAttribute('data-cb-gift') === '1', el.getAttribute('data-cb-label') || '')
          : '';
        if (el.textContent !== text) el.textContent = text;
        el.hidden = text === '';
      });
    }
    /* the legacy SSR chips: show, flip or hide each one (rules above), and
       return the smallest delay (ms, rounded up, per leg) until a leg
       reaches a store-switch end, or Infinity */
    function settleLegacy(t) {
      var E = window.EXON;
      var both = !!(E && typeof E.cbLegacyBoth === 'function' && E.cbLegacyBoth());
      var next = Infinity;
      Array.prototype.forEach.call(document.querySelectorAll(LEGACY_SEL), function (el) {
        if (!both) el.setAttribute('data-cb-off', '');
        if (el.hasAttribute('data-cb-off')) { el.hidden = true; return; }
        var ends = el.hasAttribute('data-cbc-ends') ? parseInt(el.getAttribute('data-cbc-ends'), 10) : NaN;
        if (!(ends > 0)) { el.hidden = false; return; }
        var sAfter = t.server >= ends, dAfter = t.device >= ends;
        if (!sAfter) next = Math.min(next, Math.ceil(ends - t.server));
        if (!dAfter) next = Math.min(next, Math.ceil(ends - t.device));
        if (sAfter && dAfter) {
          if (!el.hasAttribute('data-cbc-done')) {
            el.textContent = el.getAttribute('data-cbc-base') || '';
            el.setAttribute('data-cbc-done', '');
          }
          el.hidden = false;
          return;
        }
        /* the legs disagree, or a flipped chip whose legs are back before
           the end (a device clock set back): no single value is in force */
        el.hidden = sAfter !== dAfter || el.hasAttribute('data-cbc-done');
      });
      return next;
    }
    function refresh() {
      if (timer !== null) { clearTimeout(timer); timer = null; }
      paint();
      var wait = settleLegacy(clock());
      var E = window.EXON;
      var d = E && typeof E.cbChipNextDelayMs === 'function' ? E.cbChipNextDelayMs() : null;
      if (typeof d === 'number' && isFinite(d) && d < wait) wait = d;
      if (wait === Infinity) return;
      timer = setTimeout(function () { timer = null; refresh(); },
        Math.min(Math.max(wait, 0), MAX_WAIT));
    }
    (window.EXON = window.EXON || {}).cbChipsRefresh = refresh;
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh);
    else refresh();
    /* a tab that slept through a change, or a page restored from bfcache */
    document.addEventListener('visibilitychange', function () { if (!document.hidden) refresh(); });
    window.addEventListener('pageshow', refresh);
    window.addEventListener('focus', refresh);
    /* the theme editor re-renders a section in place (product cards and
       their chips included) without a page load (§A.3) */
    document.addEventListener('shopify:section:load', refresh);
  })();

  document.addEventListener('click', function (e) {
    var t = e.target;

    /* quick-add on a product card */
    var btn = t.closest && t.closest('[data-quick-add]');
    if (btn) {
      e.preventDefault();
      e.stopPropagation();
      var card = btn.closest('[data-product-card]');
      var id = card && card.getAttribute('data-variant-id');
      if (!id || !window.EXON || !window.EXON.cart) return;
      if (btn.getAttribute('aria-busy') === 'true') return;
      btn.setAttribute('aria-busy', 'true');
      window.EXON.cart
        .add({ id: Number(id), quantity: 1 })
        .catch(function () { /* surfaced by the cart drawer / toast island */ })
        .then(function () { btn.removeAttribute('aria-busy'); });
      return;
    }

    /* copy a drop discount code (the dashed voucher chip) */
    var chip = t.closest && t.closest('[data-copy-code]');
    if (chip) {
      e.preventDefault();
      e.stopPropagation();
      var code = chip.getAttribute('data-copy-code') || '';
      var done = function () {
        chip.classList.add('ok');
        setTimeout(function () { chip.classList.remove('ok'); }, 1800);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(done).catch(done);
      } else {
        done();
      }
    }
  });

  /* ------------------------------------------------------------
     Hover screenshot-motion (OWNER LOCKED — mockup ProductCard.tsx):
     cards carry up to 3 extra images in data-ss (pipe-separated).
     On mouseenter of the poster (.art) we mount <span class="ss-motion">
     with the imgs (Ken-Burns cycle lives in cards.css); on mouseleave
     the span is removed — exactly the mockup's mount/unmount. Gated on
     fine pointers; reduced-motion is handled in CSS (display:none).
     ------------------------------------------------------------ */
  var FINE_POINTER =
    window.matchMedia &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  if (FINE_POINTER) {
    var artOf = function (target, related) {
      if (!target || !target.closest) return null;
      var art = target.closest('[data-product-card][data-ss] .art');
      /* ignore moves within the same poster (mouseover/out bubble) */
      if (!art || (related && art.contains(related))) return null;
      return art;
    };

    document.addEventListener('mouseover', function (e) {
      var art = artOf(e.target, e.relatedTarget);
      if (!art || art.querySelector('.ss-motion')) return;
      var card = art.closest('[data-product-card]');
      var urls = (card.getAttribute('data-ss') || '')
        .split('|')
        .filter(Boolean)
        .slice(0, 3);
      if (!urls.length) return;
      var span = document.createElement('span');
      span.className = 'ss-motion';
      span.setAttribute('aria-hidden', 'true');
      urls.forEach(function (u) {
        var img = document.createElement('img');
        img.src = u;
        img.alt = '';
        span.appendChild(img);
      });
      /* insert right after the cover image — badges (.pct-art) and the
         quick-add button share z-index:2 and must stay painted above,
         same DOM order as the mockup */
      var cover = art.firstElementChild;
      art.insertBefore(span, cover ? cover.nextSibling : null);
      /* double rAF so the opacity transition in .ss-motion.on runs */
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          span.classList.add('on');
        });
      });
    });

    document.addEventListener('mouseout', function (e) {
      var art = artOf(e.target, e.relatedTarget);
      if (!art) return;
      var span = art.querySelector('.ss-motion');
      if (span && span.parentNode) span.parentNode.removeChild(span);
    });
  }
})();
